/**
 * A value object for representing the spaces available for a single session.
 *
 * Note that some of these numbers depend on the context. The number of spaces depends on what's in the cart,
 * and what tickets are being requested for that session. So this object should not be stored. It should be recreated
 * when needed with appropriate numbers.
 * The following properties are the exception, as they should be facts about the session not facts about the context
 * and should not change depending on the context:
 * - spacesAvailable
 * - spacesSold
 * - spacesReserved
 */
class SessionAvailability {
    /**
     * @param {number} sessionID
     * @param {number|null} spacesAvailable
     * @param {number|null} spacesSold
     * @param {number|null} spacesReserved
     * @param {number|null} spacesInCart
     * @param {number|null} spacesInOrder
     */
    constructor(
        sessionID,
        spacesAvailable = null,
        spacesSold = null,
        spacesReserved = null,
        spacesInCart = null,
        spacesInOrder = null
    ) {
        this.sessionID = sessionID;

        /**
         * The number of spaces that we know are bookable, probably due to some external factors to the number
         * of spaces. This might get set to zero if bookable resources are unavailable for example.
         * This number should be the preferred source of available spaces if it is populated.
         *
         * @type {number|null}
         */
        this.spacesBookable = null;

        /**
         * How many spaces are empty on the session.
         *
         * @type {number|null}
         */
        this.spacesAvailable = spacesAvailable;

        /**
         * How many spaces have been sold in confirmed orders for the session.
         *
         * @type {number|null}
         */
        this.spacesSold = spacesSold;

        /**
         * How many spaces are currently held in reservations.
         *
         * @type {number|null}
         */
        this.spacesReserved = spacesReserved;

        /**
         * How many spaces are currently in the cart in ProPoint for the session.
         *
         * @type {number|null}
         */
        this.spacesInCart = spacesInCart;

        /**
         * How many spaces are already sold by the order being edited on ProPoint for the session.
         *
         * @type {number|null}
         */
        this.spacesInOrder = spacesInOrder;

        /**
         * @type {boolean}
         */
        this.bookableResourcesAvailable = true;

        /**
         * @type {boolean}
         */
        this.bookableResourcesSuitable = true;

        /**
         * The qty of group bookings are associated with this session.
         *
         * @type {number}
         */
        this.groupBookingCount = 0;
    }

    /**
     * @param {number|null} spacesBookable
     *
     * @return {SessionAvailability}
     */
    setSpacesBookable(spacesBookable) {
        this.spacesBookable = spacesBookable;

        return this;
    }

    getSpacesBookable() {
        // Use the specifically supplied number if available.
        if (this.spacesBookable !== null) {
            return this.spacesBookable;
        }

        return this.spacesAvailable;
    }
}

module.exports = SessionAvailability;
